import { createContext, ReactNode, useContext, useReducer } from 'react';
import { AssessmentAnswer } from '../../../content/assessment/utils/assessmentUtils';

type QuestionnaireKey = string;
type QuestionKey = string;

type Answers = Record<QuestionKey, AssessmentAnswer>;
type AnswersByQuestionnaire = Record<QuestionnaireKey, Answers>;
type AnswerAction = {
  answers: Answers;
  questionnaireKey?: string;
  type: 'set' | 'reset';
};

type CoachingAssessmentContextType = {
  answersByQuestionnaire: AnswersByQuestionnaire;
  setAnswer: (questionnaireKey: string, questionKey: string, answer: AssessmentAnswer) => void;
  resetAnswers: () => void;
};

const CoachingAssessmentContext = createContext<CoachingAssessmentContextType>({
  answersByQuestionnaire: {},
  setAnswer: () => undefined,
  resetAnswers: () => undefined,
});

export const CoachingAssessmentConsumer = CoachingAssessmentContext.Consumer;

export const CoachingAssessmentProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [answersByQuestionnaire, dispatch] = useReducer(
    (state: AnswersByQuestionnaire, action: AnswerAction) => {
      if (action.type === 'reset') {
        return {};
      }

      if (action.questionnaireKey === undefined) {
        return state;
      }

      const { questionnaireKey } = action;

      return {
        ...state,
        [questionnaireKey]: { ...(state[questionnaireKey] ?? {}), ...action.answers },
      };
    },
    {},
  );

  const setAnswer = (
    questionnaireKey: string,
    questionKey: string,
    answer: AssessmentAnswer,
  ): void => {
    dispatch({
      answers: {
        [questionKey]: answer,
      },
      questionnaireKey,
      type: 'set',
    });
  };

  const resetAnswers = (): void => {
    dispatch({ answers: {}, type: 'reset' });
  };

  const providerValue: CoachingAssessmentContextType = {
    answersByQuestionnaire,
    setAnswer,
    resetAnswers,
  };

  return (
    <CoachingAssessmentContext.Provider value={providerValue}>
      {children}
    </CoachingAssessmentContext.Provider>
  );
};

export const useCoachingAssessmentContext = (): CoachingAssessmentContextType => {
  return useContext(CoachingAssessmentContext);
};
